import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { Trash2 } from 'lucide-react';

import { getBulkLeave, getUsers, addUsers, removeUser, removeAllUsers, deleteBulkLeave } from '../../services/bulkLeaveService';
import { parseTableParams, formatDate } from '../../utils/Utils';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import DataTable from '../../components/ui/DataTable';
import PersonCell from '../../components/ui/PersonCell';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import api from '../../services/api';

const Container = styled.div``;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  margin-bottom: 20px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
`;

const InfoItem = styled.div``;

const InfoLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #A0A8B4;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BoolBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $val }) => $val ? '#2e7d3215' : '#9e9e9e15'};
  color: ${({ $val }) => $val ? '#2e7d32' : '#9e9e9e'};
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding-top: 40px;
  overflow-y: auto;
`;

const DrawerBox = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 520px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
  margin-bottom: 40px;
`;

const DrawerTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 20px;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

export default function BulkLeaveDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const history = useHistory();

  const [bulkLeave, setBulkLeave] = useState(null);
  const [loading, setLoading] = useState(true);

  // Users table
  const [users, setUsers] = useState([]);
  const [userPagination, setUserPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [userQueryParams, setUserQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [userFilterList, setUserFilterList] = useState([]);
  const [usersFetching, setUsersFetching] = useState(true);
  const [userRefreshTrigger, setUserRefreshTrigger] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addUserIds, setAddUserIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  const [deleteBulkConfirm, setDeleteBulkConfirm] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);

  useEffect(() => {
    setLoading(true);
    getBulkLeave(id)
      .then((res) => setBulkLeave(res.data?.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setUsersFetching(true);
    const paramsToUse = userQueryParams && Object.keys(userQueryParams).length > 0
      ? userQueryParams : { pageNumber: 1, pageSize: 10 };
    getUsers(id, paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        setUsers(result?.data || []);
        setUserPagination({
          totalRowCount: result?.totalRowCount || 0,
          pageIndex: (result?.pageIndex || 1) - 1,
          pageSize: result?.pageSize || 10,
        });
      })
      .finally(() => setUsersFetching(false));
  }, [id, userQueryParams, userRefreshTrigger]);

  const refreshUsers = useCallback(() => setUserRefreshTrigger((p) => p + 1), []);

  const userColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'fullName', label: t('bulkLeave.userName'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = users[tableMeta.rowIndex];
          return <PersonCell name={v || `${row?.firstName || ''} ${row?.lastName || ''}`.trim()} profilePicture={row?.profilePicture} />;
        },
      },
    },
    { name: 'email', label: t('bulkLeave.userEmail'), options: { filter: false, sort: false } },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = users[tableMeta.rowIndex];
          return (
            <button
              onClick={(e) => { e.stopPropagation(); handleRemoveUser(row); }}
              style={{
                background: 'none', border: '1px solid #E0E4E8', borderRadius: 4,
                padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                fontFamily: "'Nunito Sans', sans-serif", fontSize: 12, color: '#004242',
              }}
            >
              <Trash2 size={13} />
            </button>
          );
        },
      },
    },
  ], [t, users]);

  const handleUserDataStateChange = useCallback((action, tableState) => {
    setUserFilterList(tableState.filterList);
    const params = parseTableParams(tableState, userColumns);
    setUserQueryParams(params);
  }, [userColumns]);

  const handleUserFilterConfirm = useCallback((newFilterList) => {
    setUserFilterList(newFilterList);
  }, []);

  const handleAddClick = useCallback(() => {
    setAddUserIds([]);
    setDrawerOpen(true);
  }, []);

  const searchEmployees = useCallback(async (q) => {
    const res = await api.get(`/v1/employees?pageSize=50&lookup=true&fullName[like]=${q}`);
    return (res.data?.data?.data || []).map((e) => ({
      id: e.userId || e.id,
      firstName: e.firstName,
      lastName: e.lastName,
      profilePicture: e.profilePicture,
    }));
  }, []);

  const handleSaveUsers = useCallback(async () => {
    setSaving(true);
    try {
      await addUsers(id, { userIds: addUserIds });
      refreshUsers();
      setDrawerOpen(false);
    } catch { /* interceptor */ }
    setSaving(false);
  }, [id, addUserIds, refreshUsers]);

  const handleRemoveUser = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.userId || row.id], name: row.fullName || row.email });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('bulkLeave.users')}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      if (deleteConfirm.ids.length === 1) {
        await removeUser(id, deleteConfirm.ids[0]);
      } else {
        await removeAllUsers(id, deleteConfirm.ids);
      }
      refreshUsers();
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [id, deleteConfirm.ids, refreshUsers]);

  const handleDeleteBulkLeave = useCallback(async () => {
    setDeletingBulk(true);
    try {
      await deleteBulkLeave(id);
      history.push('/bulk-leaves');
    } catch { /* interceptor */ }
    setDeletingBulk(false);
  }, [id, history]);

  const dataTableContent = useMemo(() => (
    <DataTable
      title={t('bulkLeave.users')}
      fetching={usersFetching}
      columns={userColumns}
      data={users}
      totalRowCount={userPagination.totalRowCount}
      pageIndex={userPagination.pageIndex}
      pageSize={userPagination.pageSize}
      onRowsDelete={handleRowsDelete}
      filterList={userFilterList}
      onFilterConfirm={handleUserFilterConfirm}
      onTableChanged={handleUserDataStateChange}
    />
  ), [
    t, usersFetching, userColumns, users, userPagination,
    handleRowsDelete, userFilterList,
    handleUserFilterConfirm, handleUserDataStateChange,
  ]);

  if (loading || !bulkLeave) return <Container />;

  return (
    <Container>
      <Breadcrumbs items={[
        { label: t('bulkLeave.title'), path: '/bulk-leaves' },
        { label: t('bulkLeave.detail') },
      ]} />

      <Card>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>{t('bulkLeave.leaveStart')}</InfoLabel>
            <InfoValue>{formatDate(bulkLeave.leaveStart, i18n.language)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('bulkLeave.leaveEnd')}</InfoLabel>
            <InfoValue>{formatDate(bulkLeave.leaveEnd, i18n.language)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('bulkLeave.cost')}</InfoLabel>
            <InfoValue>{bulkLeave.cost}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('bulkLeave.isHourly')}</InfoLabel>
            <InfoValue><BoolBadge $val={bulkLeave.isHourly}>{bulkLeave.isHourly ? t('common.yes') : t('common.no')}</BoolBadge></InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('bulkLeave.isPaid')}</InfoLabel>
            <InfoValue><BoolBadge $val={bulkLeave.isPaid}>{bulkLeave.isPaid ? t('common.yes') : t('common.no')}</BoolBadge></InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('bulkLeave.createDate')}</InfoLabel>
            <InfoValue>{formatDate(bulkLeave.createDate, i18n.language)}</InfoValue>
          </InfoItem>
        </InfoGrid>
        {bulkLeave.managerNotes && (
          <div style={{ marginTop: 16 }}>
            <InfoLabel>{t('bulkLeave.managerNotes')}</InfoLabel>
            <InfoValue style={{ fontWeight: 400 }}>{bulkLeave.managerNotes}</InfoValue>
          </div>
        )}
        <ActionBar>
          <Button variant="outline" onClick={() => setDeleteBulkConfirm(true)} style={{ color: '#d32f2f', borderColor: '#d32f2f' }}>
            <Trash2 size={16} /> {t('bulkLeave.delete')}
          </Button>
        </ActionBar>
      </Card>

      <ConfirmDialog
        isOpen={deleteBulkConfirm}
        title={t('bulkLeave.deleteTitle')}
        message={t('bulkLeave.deleteMessage')}
        onConfirm={handleDeleteBulkLeave}
        onCancel={() => setDeleteBulkConfirm(false)}
        loading={deletingBulk}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('bulkLeave.removeUser')}
        message={t('bulkLeave.removeUserMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {drawerOpen && (
        <Overlay onClick={() => setDrawerOpen(false)}>
          <DrawerBox onClick={(e) => e.stopPropagation()}>
            <DrawerTitle>{t('bulkLeave.addUsers')}</DrawerTitle>
            <FormGrid>
              <AsyncGenericSelect
                label={t('bulkLeave.employees')}
                isMulti
                value={addUserIds}
                onChange={setAddUserIds}
                onSearch={searchEmployees}
              />
            </FormGrid>
            <DialogActions>
              <Button variant="outline" onClick={() => setDrawerOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSaveUsers} disabled={saving || addUserIds.length === 0}>
                {saving ? '...' : t('common.save')}
              </Button>
            </DialogActions>
          </DrawerBox>
        </Overlay>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button onClick={handleAddClick}>+ {t('bulkLeave.addUsers')}</Button>
      </div>

      {dataTableContent}
    </Container>
  );
}
